import axios, { AxiosError, AxiosInstance } from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import { env } from '../config/env';
import { LicenceRecord } from '@lms/shared';

/**
 * API error class for structured error handling
 */
export class ApiError extends Error {
  constructor(
    message: string,
    public statusCode: number,
    public details?: any
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Configuration for retry logic
 */
const RETRY_CONFIG = {
  maxRetries: 3,
  initialDelayMs: 1000,
  maxDelayMs: 10000,
};

/**
 * Calculate exponential backoff delay
 */
function getRetryDelay(attemptNumber: number): number {
  const delay = RETRY_CONFIG.initialDelayMs * Math.pow(2, attemptNumber);
  return Math.min(delay, RETRY_CONFIG.maxDelayMs);
}

/**
 * Sleep for specified milliseconds
 */
function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Determine if an error is retryable (network errors)
 */
function isRetryableError(error: AxiosError): boolean {
  // Retry on network errors or 5xx server errors
  return !error.response || (error.response.status >= 500 && error.response.status < 600);
}

/**
 * Create axios instance with base configuration
 */
function createAxiosInstance(): AxiosInstance {
  const instance = axios.create({
    baseURL: env.apiEndpoint,
    timeout: 30000,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // Request interceptor: Add JWT token to Authorization header
  instance.interceptors.request.use(
    async (config) => {
      try {
        const session = await fetchAuthSession();
        const token = session.tokens?.idToken?.toString();
        
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      } catch (error) {
        console.error('Failed to fetch auth session:', error);
        // Continue without token - API will return 401 if auth is required
      }
      
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Response interceptor: Handle errors
  instance.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      if (error.response) {
        const { status, data } = error.response;
        
        switch (status) {
          case 401:
            // Unauthorized - authentication required or token expired
            // The app will handle this by redirecting to login
            throw new ApiError(
              'Your session has expired. Please sign in again.',
              401,
              data
            );
          
          case 403:
            // Forbidden - insufficient permissions
            const role = typeof data === 'object' && data !== null && 'requiredRole' in data
              ? (data as any).requiredRole
              : null;
            const permissionMessage = role
              ? `This action requires ${role} role. You do not have sufficient permissions.`
              : 'You do not have permission to perform this action.';
            throw new ApiError(permissionMessage, 403, data);
          
          case 400:
            // Bad Request - validation error
            let validationMessage = 'Invalid request data.';
            
            // Extract detailed validation errors if available
            if (typeof data === 'object' && data !== null) {
              if ('message' in data && typeof (data as any).message === 'string') {
                validationMessage = (data as any).message;
              } else if ('errors' in data && Array.isArray((data as any).errors)) {
                // Format Zod validation errors
                const errors = (data as any).errors;
                validationMessage = errors.map((err: any) => 
                  `${err.path?.join('.') || 'Field'}: ${err.message}`
                ).join(', ');
              }
            }
            
            throw new ApiError(validationMessage, 400, data);
          
          case 404:
            // Not Found
            throw new ApiError('The requested resource was not found.', 404, data);
          
          case 500:
          case 502:
          case 503:
          case 504:
            // Server errors - generic message to avoid exposing internal details
            throw new ApiError(
              'A server error occurred. Please try again later. If the problem persists, contact support.',
              status,
              data
            );
          
          default:
            throw new ApiError(
              `Request failed with status ${status}. Please try again.`,
              status,
              data
            );
        }
      }
      
      // Network error or timeout
      if (error.code === 'ECONNABORTED') {
        throw new ApiError(
          'Request timeout. The server took too long to respond. Please try again.',
          0
        );
      }
      
      throw new ApiError(
        'Network error. Please check your internet connection and try again.',
        0
      );
    }
  );

  return instance;
}

/**
 * Axios instance for API requests
 */
const apiClient = createAxiosInstance();

/**
 * Execute request with retry logic for network errors
 */
async function executeWithRetry<T>(
  requestFn: () => Promise<T>,
  attemptNumber = 0
): Promise<T> {
  try {
    return await requestFn();
  } catch (error) {
    if (
      error instanceof AxiosError &&
      isRetryableError(error) &&
      attemptNumber < RETRY_CONFIG.maxRetries
    ) {
      const delay = getRetryDelay(attemptNumber);
      console.log(`Retrying request after ${delay}ms (attempt ${attemptNumber + 1}/${RETRY_CONFIG.maxRetries})`);
      await sleep(delay);
      return executeWithRetry(requestFn, attemptNumber + 1);
    }
    throw error;
  }
}

/**
 * Query parameters for fetching licences
 */
export interface FetchLicencesParams {
  search?: string;
  page?: number;
  limit?: number;
}

/**
 * Response from fetchLicences API
 */
export interface FetchLicencesResponse {
  licences: LicenceRecord[];
  total: number;
  page: number;
  limit: number;
}

/**
 * Fetch all licence records with optional search and pagination
 * 
 * @param params - Query parameters for filtering and pagination
 * @returns Paginated list of licence records
 */
export async function fetchLicences(
  params?: FetchLicencesParams
): Promise<FetchLicencesResponse> {
  return executeWithRetry(async () => {
    const response = await apiClient.get<FetchLicencesResponse>('/licences', {
      params,
    });
    return response.data;
  });
}

/**
 * Update a licence record
 * 
 * @param customerKey - Unique identifier for the licence
 * @param updates - Partial licence record with fields to update
 * @returns Updated licence record
 */
export async function updateLicence(
  customerKey: string,
  updates: Partial<LicenceRecord>
): Promise<LicenceRecord> {
  return executeWithRetry(async () => {
    const response = await apiClient.patch<LicenceRecord>(
      `/licences/${encodeURIComponent(customerKey)}`,
      updates
    );
    return response.data;
  });
}

/**
 * Delete a licence record (Admin only)
 * 
 * @param customerKey - Unique identifier for the licence to delete
 * @returns Success confirmation
 */
export async function deleteLicence(customerKey: string): Promise<void> {
  return executeWithRetry(async () => {
    await apiClient.delete(`/licences/${encodeURIComponent(customerKey)}`);
  });
}

/**
 * Response from fetchApiKeyStatus API
 */
export interface ApiKeyStatusResponse {
  apiKeyId?: string;
  name?: string;
  enabled?: boolean;
  createdDate?: string;
  lastUpdatedDate?: string;
}

/**
 * Fetch API key status
 * 
 * @param apiKeyId - API Gateway key ID
 * @returns API key status information
 */
export async function fetchApiKeyStatus(apiKeyId: string): Promise<ApiKeyStatusResponse> {
  return executeWithRetry(async () => {
    const response = await apiClient.get<ApiKeyStatusResponse>(
      `/api_gw/${encodeURIComponent(apiKeyId)}`
    );
    return response.data;
  });
}

/**
 * Update API key status
 * 
 * @param apiKeyId - API Gateway key ID
 * @param enabled - Enable or disable the API key
 * @returns Updated API key status
 */
export async function updateApiKeyStatus(
  apiKeyId: string,
  enabled: boolean
): Promise<ApiKeyStatusResponse> {
  return executeWithRetry(async () => {
    const response = await apiClient.patch<ApiKeyStatusResponse>(
      `/api_gw/${encodeURIComponent(apiKeyId)}`,
      { enabled }
    );
    return response.data;
  });
}

/**
 * Export the axios instance for advanced usage
 */
export { apiClient };
